import {
  MonthView,
  Scheduler,
  SchedulerForm,
  SchedulerFormEditor,
  SchedulerItem,
} from '@progress/kendo-react-scheduler';
import '@progress/kendo-date-math/tz/Europe/Berlin';

import { useEffect, useState } from 'react';
import { graphql } from '../gql';
import { useMutation, useQuery } from 'urql';
import { cloneAndRemoveTypename } from './util.ts';
import { Dialog } from '@progress/kendo-react-dialogs';

interface MyAvailabilityProps {
  companyId: string;
}

const OwnLecturerAvailabilityEventsQuery = graphql(/* GraphQL */ `
  query ownLecturerAvailabilityEvents($companyId: String!) {
    ownLecturerAvailabilityEvents(companyId: $companyId) {
      id
      begin
      end
    }
  }
`);

const CalendarAvailabilityMutation = graphql(/* GraphQL */ `
  mutation saveAvailability(
    $changedEvents: ChangedAvailabilityEventsInput!
    $companyId: String!
  ) {
    saveAvailability(changedEvents: $changedEvents, companyId: $companyId) {
      added {
        id
        start
        end
      }
    }
  }
`);

interface CalendarEvent {
  id: string;
  start: Date;
  end: Date;
}

export const MyAvailability: React.FC<MyAvailabilityProps> = ({
  companyId,
}) => {
  const [data, setData] = useState<CalendarEvent[]>([]);

  const [, updateCalendarAvailability] = useMutation(
    CalendarAvailabilityMutation,
  );

  const [{ data: dataServer }] = useQuery({
    query: OwnLecturerAvailabilityEventsQuery,
    variables: { companyId },
    pause: companyId === undefined || companyId === null,
  });

  useEffect(() => {
    // Sync server-side availability events into editable local state.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setData(
      cloneAndRemoveTypename(
        dataServer?.ownLecturerAvailabilityEvents ?? [],
      )?.map((e) => ({
        id: e.id,
        start: new Date(e.begin),
        end: new Date(e.end),
      })),
    );
  }, [JSON.stringify(dataServer)]);

  const handleChange = (newData: {
    updated?: Array<CalendarEvent>;
    deleted?: Array<CalendarEvent>;
    created?: Array<CalendarEvent>;
  }) => {
    if (
      newData?.updated?.length ||
      newData?.deleted?.length ||
      newData?.created?.length
    ) {
      setData((oldState) => {
        let newState = structuredClone(oldState);
        if (newData?.deleted?.length) {
          newState = newState
            .map((item) =>
              newData?.deleted?.some((current) => current.id === item.id)
                ? null
                : item,
            )
            .filter(Boolean) as CalendarEvent[];
        }
        if (newData?.updated?.length) {
          newState = newState.map(
            (item) =>
              newData?.updated?.find((current) => current.id === item.id) ||
              item,
          );
        }
        return newState;
      });

      updateCalendarAvailability({
        companyId: companyId,
        changedEvents: {
          updated:
            newData.updated?.map((e) => ({
              id: e.id,
              end: e.end,
              start: e.start,
            })) ?? [],
          deleted:
            newData.deleted?.map((e) => ({
              id: e.id,
              end: e.end,
              start: e.start,
            })) ?? [],
          added:
            newData.created?.map((e) => ({
              id: e.id,
              end: e.end,
              start: e.start,
            })) ?? [],
        },
      }).then(({ data }) => {
        const dataCloned = cloneAndRemoveTypename(data?.saveAvailability);
        setData((oldState) => {
          return [
            ...oldState,
            ...((dataCloned?.added.map((e) => ({
              ...e,
              start: new Date(e.start),
              end: new Date(e.end),
            })) ?? []) as CalendarEvent[]),
          ];
        });
      });
    }
  };

  return (
    <>
      <h2>Meine Verfügbarkeit</h2>
      <div className="relative flex flex-col rounded-md border border-gray-200 bg-white p-6 md:p-8">
        <Scheduler
          editable={true}
          onDateChange={(event) => {
            console.log(event.value);
          }}
          onDataChange={handleChange}
          timezone="Europe/Berlin"
          data={data}
          height={'100%'}
          footer={() => <></>}
          form={(props) => (
            <SchedulerForm
              {...props}
              dialog={(props) => (
                <Dialog
                  {...props}
                  title={'Verfügbarkeit'}
                  appendTo={document.getElementById('root')}
                />
              )}
              editor={(props) => (
                <SchedulerFormEditor
                  {...props}
                  titleEditor={() => <></>}
                  titleLabel={() => <></>}
                  descriptionEditor={() => <></>}
                  descriptionLabel={() => <></>}
                  startTimezoneEditor={() => <></>}
                  endTimezoneEditor={() => <></>}
                  startTimezoneLabel={() => <></>}
                  startTimezoneCheckedEditor={() => <></>}
                  startTimezoneCheckedLabel={() => <></>}
                  recurrenceEditor={() => <></>}
                  className="[&_.k-form-field-wrap]:!flex [&_.k-form-field-wrap]:gap-2"
                />
              )}
            />
          )}
          item={(props) => (
            <SchedulerItem {...props} onClick={(e) => console.log(e)} />
          )}
          style={
            {
              '--kendo-font-size': '0.875em',
            } as React.CSSProperties
          }
          className="[&_.k-scheduler-body]:h-[calc(100%-50px)] [&_.k-slot-cell:not(.k-other-month):not(.k-selected)]:bg-white"
        >
          <MonthView
            selectedDateFormat="{0:D}"
            selectedShortDateFormat="{0:D}"
          />
        </Scheduler>
      </div>
    </>
  );
};
